import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { createFileRoute } from "@tanstack/react-router";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import campus from "../assets/campus-sign.webp";

export const Route = createFileRoute("/grundskola")({
  loader: async ({ context }) => {
    const data = await safeLoader("sv.grundskola.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "grundskola", "sv") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: absoluteUrl("/grundskola") },
        { property: "og:locale", content: "sv_SE" },
        ...ogImage(),
      ],
      links: seoLinks("/grundskola"),
    };
  },
  component: Grundskola,
});

function Grundskola() {
  const { text } = useContent("grundskola");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <Prose>
        <p>
          Skolan följer den svenska läroplanen och har svensktalande lärare som arbetar för att skapa
          en trygg och stimulerande lärandemiljö. På plats i skolans lokaler undervisar vi från
          förskoleklass till årskurs 6, och för högstadiet och gymnasiet erbjuder vi handledning
          genom Sofia Distans och Hermods.
        </p>

        <h2>Elevernas lärande i centrum</h2>
        <p>
          Skolan fokuserar på att utveckla elevernas akademiska, sociala och personliga förmågor
          genom olika ämnen och aktiviteter. Eleverna får en högkvalitativ svensk utbildning som ger
          dem en stabil grund för sin vidare utbildning.
        </p>

        <h2>Kontinuitet i utbildningen</h2>
        <p>
          Skolan följer den svenska läroplanen och använder svenska som undervisningsspråk, vilket
          ger eleverna en unik möjlighet att fortsätta sin svenska utbildning även om de befinner sig
          i Spanien. Det gör övergången enklare när de återvänder till Sverige eller fortsätter sin
          utbildning på andra internationella skolor.
        </p>

        <img
          src={campus}
          alt="Skylt med Swedish International Schools logotyp vid skolans entré"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={1600}
          height={1067}
        />

        <h2>Svenska traditioner och kultur</h2>
        <p>
          Skolan använder sig av svenska högtider, sånger och traditioner för att introducera
          eleverna för svensk kultur och historia. Det ger eleverna en möjlighet att lära sig om sitt
          ursprung och känna sig delaktiga i svenska traditioner även om de bor i Spanien.
        </p>
        <p>
          Skolan har också en stark gemenskap och erbjuder eleverna möjlighet att lära känna andra
          svenska barn och familjer som bor i Spanien.
        </p>

        <h2>En skoldag hos oss</h2>
        <p>
          Dagen börjar med samling i klassen och fortsätter med lektionspass i läroplanens ämnen,
          avbrutna av raster utomhus och gemensam lunch på skolan. Eftermiddagarna används ofta till
          praktiska och estetiska ämnen, idrott och arbete i mindre grupper. Efter skoldagen finns{" "}
          <a className="text-primary link-quiet" href="/fritidsverksamhet">fritids</a> för den
          som vill stanna längre.
        </p>
        <p>
          Klasserna är små, vilket gör att lärarna hinner se varje elev och kan anpassa uppgifter
          efter var eleven befinner sig. Digitala verktyg används som ett komplement till böcker och
          skrivande, inte som en ersättning.
        </p>

        <h2>Ämnen och bedömning</h2>
        <p>
          Undervisningen följer kursplanerna i den svenska läroplanen. Spanska finns med på schemat
          varje vecka och engelska läses enligt läroplanen. Kunskapsutvecklingen följs upp löpande och
          återkopplas till hemmet via utvecklingssamtal och skriftliga omdömen, så att ni vet var
          eleven står inför en eventuell återgång till skola i Sverige.
        </p>

        <h2>Stöd och trygghet</h2>
        <p>
          Klassläraren är familjens första kontakt i vardagen. Vid behov kopplas skolans{" "}
          <a className="text-primary link-quiet" href="/elevhalsoteamet">elevhälsoteam</a> in för
          kartläggning och anpassningar. Läs mer om vad vi kan och inte kan erbjuda på sidan om{" "}
          <a className="text-primary link-quiet" href="/extra-stod">extra stöd</a>.
        </p>

        <h2>Börja hos oss</h2>
        <p>
          Ansökningar tas emot löpande och det går bra att börja mitt i terminen. Terminsdatum hittar
          ni bland{" "}
          <a className="text-primary link-quiet" href="/lasarstider">läsårstiderna</a>, avgifter
          per årskurs på{" "}
          <a className="text-primary link-quiet" href="/avgifter">avgiftssidan</a> och själva
          formuläret under{" "}
          <a className="text-primary link-quiet" href="/ansokan">ansökan</a>. Vill ni se skolan
          på plats bokar vi gärna in ett besök.
        </p>


      </Prose>
      <FaqBlock items={faqFor("grundskola", "sv")} locale="sv" />


      <CtaBand />
      <div className="h-20" />
    </>
  );
}
